import { useMemo } from "react";

import useURLParams from "hooks/useURLParams";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import { fuzzySearch } from "utils/fuzzySearch";
import SearchInput from "components/SearchInput";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";

import RequiredInputs from "./components/RequiredInputs";
import { OptionalInput, RequiredInput } from "./types";
import OptionalInputs from "./components/OptionalInputs";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";
import { FILTERS_ORDER_SETTINGS_KEY_VERSION_INPUTS } from "./constants";
import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";

type ModuleVersionInputsProps = {
  inputs: Array<{
    name: string;
    description: string;
    required: boolean;
    type: string;
    default: string;
  }>;
};

const ModuleVersionInputs = ({ inputs }: ModuleVersionInputsProps) => {
  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);

  const filteredInputs = useMemo(() => {
    const results = inputs.map((input) => ({
      ...input,
      id: input.name,
    }));

    if (searchQuery) {
      return fuzzySearch(results, searchQuery.trim(), {
        keys: ["description", "name"],
        scoreThreshold: -1000,
      });
    }

    return results;
  }, [inputs, searchQuery]);

  const [requiredInputs, optionalInputs] = useMemo(
    () =>
      filteredInputs.reduce<[Array<RequiredInput>, Array<OptionalInput>]>(
        (acc, input) => {
          if (input.required) {
            acc[0].push({
              id: input.id,
              description: input.description,
              type: input.type,
            });
          } else {
            acc[1].push({
              id: input.id,
              description: input.description,
              type: input.type,
              default: input.default,
            });
          }
          return acc;
        },
        [[], []]
      ),
    [filteredInputs]
  );

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Inputs"
          tooltip="Required and optional inputs, their type, and default values."
        />

        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.INPUTS}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_INPUTS}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper gap="x-large">
        {requiredInputs.length === 0 && optionalInputs.length === 0 && (
          <EmptyState
            fullHeight
            icon={EmptystateSearchNoResultsColored}
            caption={
              searchQuery
                ? `No inputs match the search query`
                : `This module has no inputs variables`
            }
            announce={!!searchQuery}
          />
        )}
        {requiredInputs.length > 0 && <RequiredInputs inputs={requiredInputs} />}
        {optionalInputs.length > 0 && <OptionalInputs inputs={optionalInputs} />}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionInputs;
